import {
    skipToken,
    useMutation,
    useQuery,
    useQueryClient,
    type QueryClient
} from '@tanstack/react-query';
import {Team, useViewerUnsafe, Viewer, ViewerTeam} from 'bigdatr-style';
import useClientMainAuthorizedFetch from '../clientMainAuthorisedFetch';
import {
    RemoveTeamMutation,
    TeamAddNzTrialMutation,
    TeamCreateWithInvite,
    TeamIncrementDownloadCount,
    TeamInviteResendMutation,
    TeamItemQuery,
    TeamListQuery,
    TeamMemberAddMutation,
    TeamMemberInviteMutation,
    TeamMemberRemoveMutation,
    TeamMemberUpdateMutation,
    TeamRemoveInviteMutation,
    TeamResetDownloadCountMutation,
    TeamUpdateMutation,
    TeamUpdateApiAccessMonthLimitMutation,
    TeamUpdateProductsMutation
} from './graphqlQueries';
import {
    AddNzTrialInput,
    TeamMemberRole,
    TeamProductInput,
    TeamListQueryQueryVariables,
    TeamUpdateApiAccessMonthLimitMutationVariables
} from '~/graphql-react-query/graphql';
import {Product as TeamProduct} from 'bigdatr-style/src/team/Team';

type TeamCreateInvitePayload = {
    teamName: string;
    email: string;
    inviterId: string;
};

type TeamIdPayload = {
    userId: string;
    teamId: string;
};

type TeamPayload = {
    id: string;
    userId: string;
    role: TeamMemberRole;
};

export type TeamInvitePayload = {
    username: string;
    teamId: string;
    inviterId: string;
    role: TeamMemberRole;
};

type TeamUpdatePayload = {
    name: string;
    id: string;
};

type ProductUpdatePayload = {
    id: string;
    products: TeamProductInput[];
};

const teamQueryKeys = {
    teamItem: (teamId: string) => ['teamItem', teamId] as const
};

const teamListQueryKeys = {
    teamList: (teamPayload: TeamListQueryQueryVariables) => ['teamItem', teamPayload] as const
};

type TeamCachePatch = (team: Team) => Team;

function patchCachedTeam(queryClient: QueryClient, teamId: string, patch: TeamCachePatch) {
    queryClient.setQueryData<Team>(teamQueryKeys.teamItem(teamId), (cachedTeam) => {
        // undefined tells setQueryData to leave the cache unchanged.
        if (!cachedTeam) return undefined;
        return patch(cachedTeam);
    });
}

export function useIncrementTeamDownloadCount() {
    const viewer = useViewerUnsafe();
    const request = useClientMainAuthorizedFetch();
    const queryClient = useQueryClient();
    const currentTeam = viewer?.currentTeam;

    return useMutation({
        mutationFn: async () => {
            if (!currentTeam) {
                throw new Error(
                    'Cannot increment download count if the viewer doesnt have a current team'
                );
            }
            const response = await request(TeamIncrementDownloadCount, {teamId: currentTeam.id});
            const data = response.team?.teamIncrementDownloadCount;
            if (!data) throw new Error('Team not returned after incrementing download count');

            const {metadata} = data;

            // @TODO fix api to set a new date once the mutation is called.
            // at the moment it returns an updatedAt field, however it does not set a new date
            const updatedAt = new Date().toISOString();
            return new ViewerTeam({
                ...currentTeam,
                updatedAt,
                downloadCount: metadata?.downloadCount
            });
        },
        onSuccess(updatedTeam) {
            if (!viewer) return;
            // update viewer in cache
            queryClient.setQueryData(
                ['viewer', updatedTeam.id],
                new Viewer({
                    ...viewer,
                    teams: viewer.teams.map((oldTeam) => {
                        if (oldTeam.id === updatedTeam.id) {
                            return new ViewerTeam({
                                ...oldTeam,
                                updatedAt: updatedTeam.updatedAt,
                                downloadCount: updatedTeam.downloadCount
                            });
                        }
                        return oldTeam;
                    })
                })
            );
        }
    });
}

export function useTeamItem(payloadID: string) {
    const request = useClientMainAuthorizedFetch();

    return useQuery({
        queryKey: teamQueryKeys.teamItem(payloadID),
        queryFn: async () => {
            const response = await request(TeamItemQuery, {teamId: payloadID});

            if (!response?.team?.team) {
                throw new Error('Team item not returned from API');
            }

            const team = mapGraphQLUserToTeamClass(response?.team?.team);
            return team;
        }
    });
}

export function useFetchTeamListQuery(payload: TeamListQueryQueryVariables, enabled = true) {
    const request = useClientMainAuthorizedFetch();

    return useQuery({
        queryKey: teamListQueryKeys.teamList(payload),
        queryFn: enabled
            ? async () => {
                  const response = await request(TeamListQuery, {
                      search: payload.search,
                      status: payload.status,
                      country: payload.country,
                      industryList: payload.industryList
                  });

                  if (!response?.team?.teamList) {
                      throw new Error('Team item list not returned from API');
                  }

                  const team = response?.team?.teamList.map((team) =>
                      mapGraphQLUserToTeamClass(team)
                  );
                  return team;
              }
            : skipToken
    });
}

export function useTeamCreateWithInvite() {
    const request = useClientMainAuthorizedFetch();

    return useMutation({
        mutationFn: async (payload: TeamCreateInvitePayload) => {
            const response = await request(TeamCreateWithInvite, payload);
            const data = response.team?.teamCreateWithInvite;
            if (!data) throw new Error('API failed to create team.');
            return data;
        }
    });
}

export function useTeamInviteResend() {
    const request = useClientMainAuthorizedFetch();

    return useMutation({
        mutationFn: async (payloadID: string) => {
            const response = await request(TeamInviteResendMutation, {inviteId: payloadID});
            const data = response.team;
            if (!data) throw new Error('API failed to resent invite.');
            return data;
        }
    });
}

export function useTeamInviteRemove() {
    const request = useClientMainAuthorizedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (input: {inviteId: string; teamId: string}) => {
            const response = await request(TeamRemoveInviteMutation, {inviteId: input.inviteId});
            const data = response.team;
            if (!data) throw new Error('API failed to remove invite.');
            return {isInviteRemoved: data, teamId: input.teamId};
        },
        onSuccess({teamId}) {
            queryClient.invalidateQueries({queryKey: teamQueryKeys.teamItem(teamId)});
        }
    });
}

export function useTeamMemberRemove() {
    const request = useClientMainAuthorizedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: TeamIdPayload) => {
            const response = await request(TeamMemberRemoveMutation, {
                teamId: payload.teamId,
                userId: payload.userId
            });
            const data = response.team;
            if (!data) throw new Error('API failed to remove team member.');
            return {isTeamRemoveUser: data, teamId: payload.teamId};
        },
        onSuccess({teamId}) {
            queryClient.invalidateQueries({queryKey: teamQueryKeys.teamItem(teamId)});
        }
    });
}

export function useTeamMemberUpdate() {
    const request = useClientMainAuthorizedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: TeamPayload) => {
            const response = await request(TeamMemberUpdateMutation, {
                id: payload.id,
                role: payload.role,
                userId: payload.userId
            });
            const data = response.team;
            if (!data) throw new Error('API failed to update team member.');
            return {teamUpdateUserData: data, teamId: payload.id};
        },
        onSuccess({teamId}) {
            queryClient.invalidateQueries({queryKey: teamQueryKeys.teamItem(teamId)});
        }
    });
}

export function useTeamResetDownloadCount() {
    const request = useClientMainAuthorizedFetch();

    return useMutation({
        mutationFn: async (teamId: string) => {
            const response = await request(TeamResetDownloadCountMutation, {
                teamId: teamId
            });
            const data = response.team?.teamResetDownloadCount;
            if (!data) throw new Error('API failed to reset team download count.');
            return data;
        }
    });
}

export function useTeamMemberAdd() {
    const request = useClientMainAuthorizedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (team: TeamPayload[]) => {
            return await Promise.all(
                team.map(
                    async (member) =>
                        await request(TeamMemberAddMutation, {
                            member: {teamId: member.id, userId: member.userId, role: member.role}
                        })
                )
            ).then((response) => {
                const data = response;
                if (!data) throw new Error('API failed to add team member.');
                return {teamMemberAddData: data, teamId: team[0].id};
            });
        },
        onSuccess({teamId}) {
            queryClient.invalidateQueries({queryKey: teamQueryKeys.teamItem(teamId)});
        }
    });
}

export function useTeamMemberInvite() {
    const request = useClientMainAuthorizedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (team: TeamInvitePayload[]) => {
            return await Promise.all(
                team.map((teamData) => request(TeamMemberInviteMutation, {invite: teamData}))
            ).then((response) => {
                const data = response;
                if (!data) throw new Error('API failed to invite team member.');
                return {teamMemberInvite: data, teamId: team[0].teamId};
            });
        },
        onSuccess({teamId}) {
            queryClient.invalidateQueries({queryKey: teamQueryKeys.teamItem(teamId)});
        }
    });
}

export function useRemoveTeam() {
    const request = useClientMainAuthorizedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (teamID: string) => {
            const response = await request(RemoveTeamMutation, {teamId: teamID});
            const data = response.team?.teamRemove;
            if (!data) throw new Error('API failed to remove team.');
            return {isTeamRemoved: data, teamId: teamID};
        },
        onSuccess({teamId}) {
            queryClient.invalidateQueries({queryKey: teamQueryKeys.teamItem(teamId)});
            queryClient.invalidateQueries({
                queryKey: teamListQueryKeys.teamList({search: '', status: null})
            });
        }
    });
}

export function useTeamUpdate() {
    const request = useClientMainAuthorizedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: TeamUpdatePayload) => {
            const response = await request(TeamUpdateMutation, {
                id: payload.id,
                name: payload.name
            });
            const data = response.team?.teamUpdate;
            if (!data) throw new Error('API failed to update team.');
            return {teamUpdate: data, teamId: payload.id};
        },
        onSuccess({teamId}) {
            queryClient.invalidateQueries({queryKey: teamQueryKeys.teamItem(teamId)});
        }
    });
}

export function useTeamUpdateProducts() {
    const request = useClientMainAuthorizedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: ProductUpdatePayload) => {
            const response = await request(TeamUpdateProductsMutation, {
                id: payload.id,
                products: payload.products
            });
            const data = response.team?.teamUpdateProducts;
            if (!data) throw new Error('API failed to update team products.');
            return data;
        },
        onSuccess(updatedProducts, updateInput) {
            patchCachedTeam(queryClient, updateInput.id, (cachedTeam) => {
                return new Team({
                    ...cachedTeam,
                    products: updatedProducts.flatMap((product) => {
                        if (product) return [product as TeamProduct];
                        return [];
                    })
                });
            });
        }
    });
}

export function useTeamUpdateApiAccessMonthLimit() {
    const request = useClientMainAuthorizedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: TeamUpdateApiAccessMonthLimitMutationVariables) => {
            const response = await request(TeamUpdateApiAccessMonthLimitMutation, payload);
            const data = response.team?.teamUpdateApiAccessMonthLimit;
            if (!data) throw new Error('API failed to update API access month limit.');
            return {metadata: data.metadata, teamId: payload.teamId};
        },
        onSuccess({metadata, teamId}) {
            patchCachedTeam(queryClient, teamId, (cachedTeam) => {
                return new Team({
                    ...cachedTeam,
                    metadata
                });
            });
        }
    });
}

export function useTeamAddNzTrial() {
    const request = useClientMainAuthorizedFetch();

    return useMutation({
        mutationFn: async (payload: AddNzTrialInput) => {
            const response = await request(TeamAddNzTrialMutation, {input: payload});
            const data = response.team?.teamAddNzTrial;
            if (!data) throw new Error('API failed to return NZ trial.');
            return data;
        }
    });
}

// helper
export function mapGraphQLUserToTeamClass(raw: any): Team {
    return new Team(raw);
}
